// MenuAreaSelect — searchable combobox for the Dev Tracker "menu area" field.
//
// Fetches `/api/dev/menu-areas` once. Type to filter. Pick one, OR type a
// brand-new name and hit the "+ Add" row to create a custom area (persisted
// to `dev_menu_areas`), then select it. Renders as a coloured chip trigger.

import { useEffect, useMemo, useRef, useState } from "react";
import api from "../../lib/api";
import { X, Plus, Tag } from "lucide-react";

export default function MenuAreaSelect({
  value,             // area id (or null)
  valueLabel,        // display label fallback if the id isn't in the fetched list
  onChange,          // (area | null) — {id, label, colour, system} or null
  compact = false,   // narrow variant (used in the item detail aside)
  system = null,     // "1" | "2" | null (null = show all)
}) {
  const [areas, setAreas] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const q = system ? `?system=${system}` : "";
    api.get(`/dev/menu-areas${q}`).then(({ data }) => alive && setAreas(data)).catch(() => {});
    return () => { alive = false; };
  }, [system]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const current = useMemo(
    () => areas.find((a) => a.id === value) || (value ? { id: value, label: valueLabel, colour: "#94a3b8" } : null),
    [areas, value, valueLabel],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return areas;
    return areas.filter((a) => a.label.toLowerCase().includes(q));
  }, [areas, query]);

  const exactMatch = useMemo(
    () => areas.find((a) => a.label.trim().toLowerCase() === query.trim().toLowerCase()),
    [areas, query],
  );

  const pick = (area) => {
    onChange(area);
    setOpen(false);
    setQuery("");
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  const addCustom = async () => {
    const label = query.trim();
    if (!label || saving) return;
    setSaving(true);
    try {
      const body = system ? { label, system } : { label };
      const { data } = await api.post("/dev/menu-areas", body);
      setAreas((prev) => (prev.some((a) => a.id === data.id) ? prev : [...prev, data]));
      pick(data);
    } finally {
      setSaving(false);
    }
  };

  const chipSize = compact ? "text-[11px] px-2 py-1" : "text-xs px-2.5 py-1.5";

  return (
    <div className="relative" ref={wrapRef} data-testid="menu-area-select">
      {current ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${chipSize}`}
          style={{
            borderColor: current.colour + "66",
            background: current.colour + "22",
            color: current.colour,
          }}
          data-testid="menu-area-chip"
        >
          <Tag className="w-3 h-3" />
          <span>{current.label}</span>
          <span
            onClick={clear}
            className="ml-1 opacity-60 hover:opacity-100"
            data-testid="menu-area-clear"
          ><X className="w-3 h-3" /></span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`inline-flex items-center gap-1.5 rounded-full border border-dashed border-white/15 text-slate-400 hover:text-white hover:border-white/25 ${chipSize}`}
          data-testid="menu-area-empty"
        >
          <Plus className="w-3 h-3" /> Set menu area
        </button>
      )}

      {open && (
        <div
          className="absolute z-50 mt-1.5 left-0 min-w-[240px] max-w-[320px] rounded-lg border border-white/10 bg-slate-900/95 backdrop-blur-sm shadow-2xl p-1.5"
          data-testid="menu-area-popover"
        >
          <input
            autoFocus
            type="text"
            placeholder="Search or type a new name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="input-dark w-full text-sm mb-1.5"
            data-testid="menu-area-search"
          />
          <div className="max-h-64 overflow-y-auto space-y-0.5">
            {filtered.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => pick(a)}
                className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5"
                data-testid={`menu-area-option-${a.slug || a.id}`}
              >
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: a.colour }} />
                <span className="text-sm text-slate-100 flex-1">{a.label}</span>
                {!a.is_seed && <span className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">custom</span>}
              </button>
            ))}
            {filtered.length === 0 && !query && (
              <div className="text-[11px] text-slate-500 px-2 py-2">No areas defined</div>
            )}
            {query.trim() && !exactMatch && (
              <button
                type="button"
                onClick={addCustom}
                disabled={saving}
                className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-emerald-500/10 border-t border-white/5 mt-1"
                data-testid="menu-area-add-custom"
              >
                <Plus className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-sm text-emerald-300">Add "<b>{query.trim()}</b>"</span>
                {saving && <span className="text-[10px] text-slate-500 ml-auto">saving…</span>}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
